import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core";
import ModalLayout from "@View/components/ModalLayout";
import DefaultInput from "@View/components/formInputs/DefaultInput";
import DefaultRangeSlider from "@View/components/formInputs/DefaultRangeSlider";
import ButtonSave from "@View/components/buttons/ButtonSave";
import getAPI from "@API/index";

const useStyles = makeStyles(() => ({
  errorText: {
    fontSize: "12px",
    color: "rgba(164, 36, 28, 1)",
    marginBottom: "8px",
  },
}));

const initialForm = {
  inn: "",
  borrowerName: "",
  borrowerEmail: "",
  responsibleEmployeeName: "",
  responsibleEmployeePhone: "",
  ceoPhone: "",
  creditProduct: "",
  requestedAmount: "",
  requestedTermMonths: 12,
};

const TERM_MONTHS_MIN = 1;
const TERM_MONTHS_MAX = 60;

const NewLeadModal = ({ customerId, open, handleClose, onCreated }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setErrors({});
      setGlobalError("");
    }
  }, [open]);

  const setField = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const validate = () => {
    const result = {};
    if (!form.inn.trim()) result.inn = "validation.INNRequired";
    if (!form.borrowerName.trim()) result.borrowerName = "validation.BorrowerNameRequired";
    if (!form.borrowerEmail.trim()) result.borrowerEmail = "validation.EmailRequired";
    if (form.requestedAmount === "" || Number(form.requestedAmount) <= 0)
      result.requestedAmount = "validation.RequestedAmountRequired";
    if (form.requestedTermMonths === "" || Number(form.requestedTermMonths) <= 0)
      result.requestedTermMonths = "validation.RequestedTermMonthsRequired";
    return result;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setGlobalError("");
      return;
    }
    if (saving) return;

    const payload = {
      inn: form.inn.trim(),
      borrowerName: form.borrowerName.trim(),
      borrowerEmail: form.borrowerEmail.trim(),
      responsibleEmployeeName: form.responsibleEmployeeName.trim() || null,
      responsibleEmployeePhone: form.responsibleEmployeePhone.trim() || null,
      ceoPhone: form.ceoPhone.trim() || null,
      creditProduct: form.creditProduct.trim() || null,
      requestedAmount: Number(form.requestedAmount),
      requestedTermMonths: Number(form.requestedTermMonths),
    };

    setSaving(true);
    setGlobalError("");
    getAPI()
      .request({
        method: "post",
        url: "/api/lead",
        params: customerId ? { customerId } : undefined,
        data: payload,
        suppressError: false,
      })
      .then((response) => {
        if (response?.data) {
          onCreated(response.data);
          handleClose();
        }
      })
      .catch((e) => {
        const message = e?.response?.data?.error || t("errors.saveFailed", "Не удалось сохранить. Попробуйте ещё раз.");
        setGlobalError(message);
      })
      .finally(() => setSaving(false));
  };

  const footer = (
    <>
      {globalError && <div className={classes.errorText}>{globalError}</div>}
      <ButtonSave marginTop="0" disabled={saving}>buttons.Save</ButtonSave>
    </>
  );

  return (
    <ModalLayout
      open={open}
      onClose={handleClose}
      title="h1.NewLead"
      onSubmit={handleSubmit}
      footer={footer}
    >
      <DefaultInput
        label="inputs.CompanyName"
        value={form.borrowerName}
        onChange={setField("borrowerName")}
        error={!!errors.borrowerName}
        helperText={errors.borrowerName}
      />
      <DefaultInput
        label="inputs.INN"
        value={form.inn}
        onChange={setField("inn")}
        error={!!errors.inn}
        helperText={errors.inn}
      />
      <DefaultInput
        label="inputs.BorrowerEmail"
        value={form.borrowerEmail}
        onChange={setField("borrowerEmail")}
        error={!!errors.borrowerEmail}
        helperText={errors.borrowerEmail}
      />
      <DefaultInput
        label="inputs.ResponsibleEmployeeName"
        value={form.responsibleEmployeeName}
        onChange={setField("responsibleEmployeeName")}
      />
      <DefaultInput
        label="inputs.ResponsibleEmployeePhone"
        value={form.responsibleEmployeePhone}
        onChange={setField("responsibleEmployeePhone")}
      />
      <DefaultInput
        label="inputs.CeoPhone"
        value={form.ceoPhone}
        onChange={setField("ceoPhone")}
      />
      <DefaultInput
        label="inputs.CreditProduct"
        value={form.creditProduct}
        onChange={setField("creditProduct")}
      />
      <DefaultInput
        label="inputs.RequestedAmount"
        value={form.requestedAmount}
        onChange={setField("requestedAmount")}
        type="number"
        error={!!errors.requestedAmount}
        helperText={errors.requestedAmount}
      />
      <DefaultRangeSlider
        label="inputs.TermMonths"
        value={form.requestedTermMonths}
        onChange={setField("requestedTermMonths")}
        min={TERM_MONTHS_MIN}
        max={TERM_MONTHS_MAX}
        error={!!errors.requestedTermMonths}
        helperText={errors.requestedTermMonths}
      />
    </ModalLayout>
  );
};

export default NewLeadModal;
